import { InputAdornment, TextField } from "@mui/material";
import { Col, Row, Form } from "react-bootstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import React, { useEffect, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useLoader } from "../../context/LoaderContext";
import {
  getColumHeadersSelections,
  getProcessStages,
  saveColumnHeaderSelection,
  userActivity,
} from "../../api/columnHeaderSelection/columnHeaderSelectionApi";
import CustomSnackbar, {
  CustomNotificationHandle,
} from "../Notification/Notification";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../Common/SnackbarContext";
import LocalStorageUtil from "../../utils/localStorage";

function not(a: readonly number[], b: readonly number[]) {
  return a.filter((value) => !b.includes(value));
}

function intersectionLeft(a: readonly number[], b: any) {
  return b.filter((column: any) =>
    a.includes(column.metadataItemId || column?.metadataSelectedItemId)
  );
}

function intersectionRight(a: readonly number[], b: any) {
  return b.filter((column: any) =>
    a.includes(column?.metadataSelectedItemId || column?.metadataItemId)
  );
}

interface ColumnHeadersProps {
  businessUnitData: any;
}

const ColumnHeaders: React.FC<ColumnHeadersProps> = ({ businessUnitData }) => {
  const snackbarRef = useRef<CustomNotificationHandle>(null);
  const { showSnackbar } = useSnackbar();
  const [searchTermLeft, setSearchTermLeft] = useState("");
  const [searchTermRight, setSearchTermRight] = useState("");
  const [history, setHistory] = useState<
    Array<{
      left: readonly number[];
      right: readonly number[];
      checked: readonly number[];
    }>
  >([]);
  const [data] = useState<any>([]);
  const [checked, setChecked] = React.useState<readonly number[]>([]);
  const [left, setLeft] = useState<readonly number[]>([]);
  const [columnHeaders, setColumnHeaders] = useState<readonly any[]>([]);
  const [right, setRight] = useState<any>([]);
  const [formData, setFormData] = useState({
    processStageName: data[0]?.processStageName || "",
  });
  const leftChecked = intersectionLeft(checked, left);
  const rightChecked = intersectionRight(checked, right);
  const { setLoading } = useLoader();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [metadataGroupId, setMetadataGroupId] = useState(null);
  const navigate = useNavigate();

  const handleAllRight = () => {
    saveToHistory();
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setRight((prevRight: any) => [...prevRight, ...leftChecked]);
    setLeft(not(left, leftChecked));
    setChecked([]);
  };

  const handleCheckedLeft = () => {
    setLeft((prevLeft) => [...prevLeft, ...rightChecked]);
    setRight(not(right, rightChecked));
    setChecked([]);
  };

  const handleAllLeft = () => {
    saveToHistory();
    setLeft(left.concat(right));
    setRight([]);
  };

  const saveToHistory = () => {
    setHistory((prev) => [...prev, { left, right, checked }]);
  };

  const undoLastAction = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setLeft(lastState.left);
      setRight(lastState.right);
      setChecked(lastState.checked);
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  const customListLeft = (items: readonly number[], searchTermLeft: string) => {
    const handleToggle = (
      id: number,
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      const index = checked.indexOf(id);
      const newChecked = [...checked];

      if (event.ctrlKey) {
        if (index === -1) newChecked.push(id);
        else newChecked.splice(index, 1);
      }
      if (!event.ctrlKey) {
        // NOSONAR
        if (index === -1 || checked.length > 1) {
          newChecked.splice(0, newChecked.length, id);
        } else {
          newChecked.splice(index, 1);
        }
      }

      setChecked(newChecked);
    };

    const filteredItems = items.filter((item: any) =>
      item?.metadataItemName
        ?.toLowerCase()
        .includes(searchTermLeft?.toLowerCase())
    );

    return (
      <Paper sx={{ width: "100%", height: 490, overflow: "auto" }}>
        <ul
          style={{ maxHeight: 490, overflowY: "auto", padding: 0, margin: 0 }}
        >
          {filteredItems.length === 0 ? (
            <li>
              <ListItemText sx={{ textAlign: "center", color: "gray" }}>
                No items available...
              </ListItemText>
            </li>
          ) : (
            filteredItems.map((item: any) => {
              const id = item?.metadataItemId || item?.metadataSelectedItemId;
              const isSelected = checked.includes(id);

              return (
                <li key={id} style={{ listStyle: "none" }}>
                  <ListItemButton
                    role="listitem" // NOSONAR
                    onClick={(e) => handleToggle(id, e)}
                    sx={{
                      backgroundColor: isSelected
                        ? "rgb(77, 123, 167)"
                        : "transparent",
                      border: isSelected ? "1px solid darkblue" : "",
                      color: isSelected ? "white" : "default",
                      "&:hover": {
                        backgroundColor: isSelected ? "darkblue" : "lightgray",
                      },
                    }}
                  >
                    <ListItemText primary={item.metadataItemName} />
                  </ListItemButton>
                </li>
              );
            })
          )}
        </ul>
      </Paper>
    );
  };

  const customListRight = (
    items: readonly number[],
    searchTermRight: string
  ) => {
    const handleToggle = (
      id: number,
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      const index = checked.indexOf(id);
      let newChecked = [...checked];

    if (event.ctrlKey) {
    // Multi-select toggle
    if (index === -1) {
      newChecked.push(id);
    } else {
      newChecked.splice(index, 1);
    }
    } else if (index === -1 || checked.length > 1) {
    // Single-select mode: select only this id
    newChecked = [id];
    } else {
    // Single-select mode: deselect all
    newChecked = [];
    }

      setChecked(newChecked);
    };

    const filteredItems = items.filter((item: any) =>
      item?.metadataItemName
        ?.toLowerCase()
        .includes(searchTermRight?.toLowerCase())
    );

    return (
      <Paper sx={{ width: "100%", height: 490, overflow: "auto" }}>
        <ul
          style={{ maxHeight: 490, overflowY: "auto", padding: 0, margin: 0 }}
        >
          {filteredItems.length === 0 ? (
            <li>
              <ListItemText sx={{ textAlign: "center", color: "gray" }}>
                No items available...
              </ListItemText>
            </li>
          ) : (
            filteredItems.map((item: any) => {
              const id = item?.metadataSelectedItemId || item?.metadataItemId;
              const isSelected = checked.includes(id);

              return (
                <li key={id} style={{ listStyle: "none" }}>
                  <ListItemButton
                    onClick={(e) => handleToggle(id, e)}
                    sx={{
                      backgroundColor: isSelected
                        ? "rgb(77, 123, 167)"
                        : "transparent",
                      border: isSelected ? "1px solid darkblue" : "",
                      color: isSelected ? "white" : "default",
                      "&:hover": {
                        backgroundColor: isSelected ? "darkblue" : "lightgray",
                      },
                    }}
                  >
                    <ListItemText primary={item.metadataItemName} />
                  </ListItemButton>
                </li>
              );
            })
          )}
        </ul>
      </Paper>
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTermLeft(event.target.value);
    setChecked([]);
  };

  const handleSearchChangeRight = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTermRight(event.target.value);
    setChecked([]);
  };

  const handleDropdownChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    const selectedProcessStage = columnHeaders.find(
      (processStage: any) => processStage.processStageName === value
    );
    if (selectedProcessStage) {
      getAllColumnHeaders(selectedProcessStage.processStageId);
      setMetadataGroupId(selectedProcessStage.processStageId);
    } else {
      getAllColumnHeaders();
    }
  };

  const getAllColumnHeaders = async (MetadataGroupId: number = 0) => {
    setLoading(true);

    const payloadforColumnHeaders = {
      AccountId:
        businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      ...(MetadataGroupId !== undefined && { MetadataGroupId }),
      UserId: LocalStorageUtil.getItem("userId"),
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };

    try {
      const data = await getColumHeadersSelections(payloadforColumnHeaders);
      const rightItems = data.metaDataModel
        .filter((e: any) => e.metadataSelectedItemName != null)
        .map((e: any) => ({
          ...e,
          metadataItemName: e.metadataSelectedItemName,
        }));
      setRight(rightItems);
      const leftItems = data.metaDataModel.filter(
        (e: any) =>
          !rightItems.some(
            (r: any) => r.metadataItemName === e.metadataItemName
          )
      );
      setLeft(leftItems);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setFormData({
        processStageName: data[0]?.processStageName || "",
      });
    }
  }, [data]);

  const getAllProcessStages = async () => {
    setLoading(true);
    const payload = {
      AccountId:
        businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getProcessStages(payload);
      setColumnHeaders(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllProcessStages();
  }, [businessUnitData]);

  useEffect(() => {
    setFormData({ processStageName: "" });
    getAllColumnHeaders();
  }, [businessUnitData]);

  const handleConfirmDialogClose = (confirm: boolean) => {
    setConfirmDialogOpen(false);
    if (confirm) {
      navigate("/");
    } else {
      setConfirmDialogOpen(false);
    }
  };

  const handelCancel = () => {
    setConfirmDialogOpen(true);
  };

  const handleSave = async () => {
    if (!right || right.length === 0) {
      snackbarRef.current?.showSnackbar("Please select an item before saving!");
      return;
    }
    const uniqueRight = Array.from(
      new Map(
        (right || []).map((g: any) => {
          const key =
            g.metadataSelectedItemId === 0
              ? g.metadataItemId
              : g.metadataSelectedItemId;
          return [key, g];
        })
      ).values()
    );
    const groupIds = uniqueRight.map((g: any) => {
      const columnHeaderValue =
        g.metadataSelectedItemId === 0
          ? g.metadataItemId
          : g.metadataSelectedItemId;

      return {
        columnHeaderValue: columnHeaderValue,
        customValue: g.iscustomizable === true ? 1 : 0,
      };
    });
    const payload = {
      UserId: Number(LocalStorageUtil.getItem("userId")),
      MetadataItemValuesId: groupIds,
      MetadataGroupId: metadataGroupId,
      contextName: LocalStorageUtil.getItem("contextName"),
    };
    try {
      setLoading(true);
      await saveColumnHeaderSelection(payload);
      showSnackbar("Data saved successfully");
      setFormData({
        processStageName: "",
      });
      setChecked([]);
      setLoading(false);
      handleUserActivity();
    } catch (error) {
      console.error("Error while fetching:", error);
      showSnackbar("Failed to Save Data", true);
    } finally {
      setLoading(false);
    }
  };

  const handleUserActivity = async () => {
    const uniqueRight = Array.from(
      new Map(
        (right || []).map((g: any) => {
          const key =
            g.metadataSelectedItemId === 0
              ? g.metadataItemId
              : g.metadataSelectedItemId;
          return [key, g];
        })
      ).values()
    );
    const groupIds = uniqueRight.map((g: any) => {
      const columnHeaderValue =
        g.metadataSelectedItemId === 0
          ? g.metadataItemId
          : g.metadataSelectedItemId;

      return {
        columnHeaderValue: columnHeaderValue,
        customValue: g.iscustomizable === true ? 1 : 0,
      };
    });
    const payload = {
      userId: Number(LocalStorageUtil.getItem("userId")),
      contextName: LocalStorageUtil.getItem("contextName"),
      paramGetUrl: "api/MetaDataMapping/ColumnHeaderSelectionInsert",
      MetadataItemValuesId: groupIds,
      MetadataGroupId: metadataGroupId,
    };
    try {
      await userActivity(payload);
    } catch (error) {
      console.error("Error in handleUserActivity:", error);
    }
  };

  return (
    <div>
      <NavigationBar
        title="Column Headers"
        text="Create Column Headers Selection"
      />
      <div className="ag-theme-quartz-dark" style={{ marginLeft: "5vh" }}>
        <h4
          className="form-heading"
          style={{ color: "var(--aggrid-text-color)", fontSize: "12px" }}
        >
          COLUMN HEADERS SELECTION
        </h4>
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formCustomer">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Queue Type<span className="required">*</span>
                </Form.Label>
                <Form.Select
                  data-testid="form-customer-select-column-header"
                  name="processStageName"
                  value={formData.processStageName}
                  onChange={handleDropdownChange}
                  style={{
                    height: "30px",
                    fontSize: "11px",
                  }}
                >
                  <option value="">Select</option>
                  {columnHeaders.map((processStage: any) => (
                    <option
                      key={processStage.processStageId}
                      value={processStage.processStageName}
                    >
                      {processStage.processStageName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)", fontSize: "11px" }}
              >
                Available<span className="required">*</span>
              </Form.Label>
              <TextField
                fullWidth
                placeholder="Search"
                value={searchTermLeft}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  marginBottom: "10px",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    height: "35px",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#888",
                    },
                    "& input": {
                      fontSize: "11px",
                    },
                  },
                }}
                variant="outlined"
              />
              <div>{customListLeft(left, searchTermLeft)}</div>
            </Col>
            <Col
              md={1}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="move selected right"
                className="arrow-button"
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="move selected left"
                className="arrow-button"
              >
                &lt;
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleAllRight}
                disabled={left.length === 0}
                aria-label="move all right"
                className="arrow-button"
              >
                ≫
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleAllLeft}
                disabled={right.length === 0}
                aria-label="move all left"
                className="arrow-button"
              >
                ≪
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={undoLastAction}
                disabled={history.length === 0}
                aria-label="undo last action"
                className="arrow-button"
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    fontSize: "14px",
                    height: "14px",
                    width: "14px",
                  }}
                />
              </Button>
            </Col>
            <Col md={4}>
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)", fontSize: "11px" }}
              >
                Enabled<span className="required">*</span>
              </Form.Label>
              <TextField
                fullWidth
                placeholder="Search"
                value={searchTermRight}
                onChange={handleSearchChangeRight}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  "& .MuiOutlinedInput-root": {
                    height: "35px",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#888",
                    },
                    "& input": {
                      fontSize: "11px",
                    },
                  },
                }}
                style={{
                  fontSize: "11px",
                }}
                variant="outlined"
              />
              <div>{customListRight(right, searchTermRight)}</div>
            </Col>
          </Row>
          <Row>
            <Col
              md={9}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              <Button
                variant="contained"
                className="update-button"
                onClick={handleSave}
              >
                SAVE
              </Button>
              <Button
                variant="contained"
                className="cancel-button"
                onClick={handelCancel}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
        <CustomSnackbar ref={snackbarRef} />
        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={handleConfirmDialogClose}
          title={"Column Headers - Create Column Headers Selection"}
          content="Are you sure you want to Cancel?"
        />
      </div>
    </div>
  );
};

export default ColumnHeaders;
